import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import * as XLSX from "xlsx";
import { Loader, Eye, Plus, Upload } from "lucide-react";
import Config from "../config.js";
import "../styles/Templates.css";

const CERTIFICATE_DOWNLOAD_SLUG = {
  "Bedienerausweis für Hubarbeitsbühnen": "Unterweisung_Hubarbeitsbuehnen",
  "Sachkundenachweis TRGS 519": "Sachkundenachweis_TRGS519_ASI",
};

function formatValueToDdmmyy(value) {
  if (value == null || String(value).trim() === "") return null;
  const s = String(value).trim();
  const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  if (iso) {
    const [, y, mo, d] = iso;
    return `${d}${mo}${y.slice(-2)}`;
  }
  const de = /^(\d{1,2})\.(\d{1,2})\.(\d{4})\b/.exec(s);
  if (de) {
    const [, d, mo, y] = de;
    return `${d.padStart(2, "0")}${mo.padStart(2, "0")}${y.slice(-2)}`;
  }
  const deShort = /^(\d{1,2})\.(\d{1,2})\.(\d{2})\b/.exec(s);
  if (deShort) {
    const [, d, mo, y] = deShort;
    return `${d.padStart(2, "0")}${mo.padStart(2, "0")}${y}`;
  }
  return null;
}

function currentDateDdmmyy() {
  const now = new Date();
  const day = String(now.getDate()).padStart(2, "0");
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const year = String(now.getFullYear()).slice(-2);
  return `${day}${month}${year}`;
}

function getPreviewDownloadDateDdmmyy(templateName, dummyRow, requiredFieldsMap) {
  const req = requiredFieldsMap[templateName] || [];
  const tryFields = [];
  if (req.includes("prufungsdatum")) tryFields.push("prufungsdatum");
  if (req.includes("startdatum")) tryFields.push("startdatum");
  for (const field of tryFields) {
    const v = formatValueToDdmmyy(dummyRow[field]);
    if (v) return v;
  }
  return currentDateDdmmyy();
}

function getDownloadTypeSlug(certificateType) {
  if (CERTIFICATE_DOWNLOAD_SLUG[certificateType]) {
    return CERTIFICATE_DOWNLOAD_SLUG[certificateType];
  }
  return certificateType.replace(/ /g, "_");
}

const Templates = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [isGenerating, setIsGenerating] = useState({});
  const [status, setStatus] = useState("");
  const [showUpload, setShowUpload] = useState(false);
  const fileInputRef = useRef(null);

  // List of supported certificate types
  const certificateTypes = [
    "Namensschild",
    "Verschraubungsmonteur",
    "Flanschmonteur",
    "PSAgA",
    "Hubarbeitsbühnen",
    "Anschlagen von Lasten",
    "Atemschutzgeräte",
    "Brandschutzbeauftragter",
    "SiGeKo",
    "SCC Document 16",
    "EHS Manager",
    "Sifa LEK 1-5",
    "Sifa LEK 6",
    "Sifa Nachweis",
    "Sifa Abschluss",
    "Sifa Lernbegleiter",
    "Sachkundenachweis TRGS 519",
  ];

  // Dummy data for preview (using frontend field names to match requiredFields)
  const dummyData = {
    geschlecht: "Herr",
    vorname: "Vorname",
    nachname: "Nachname",
    gebDatum: "01.01.1990",
    geburtsort: "Ludwigshafen",
    startdatum: "01.01.2025",
    enddatum: "05.01.2025",
    endPrufungsdatum: "05.01.2025",
    prufungsdatum: "05.01.2025",
    prufungsort: "Ludwigshafen",
    gultigBis: "01.01.2028",
    ausgestelltAm: "01.01.2025",
    prufer: "Snezana Heckmann",
    bescheidDatum: "15.01.2025",
    aktenzeichen: "Az. 1234-56",
  };

  // Required fields mapping (same as CreateCertificate.js)
  const requiredFields = {
    Namensschild: ["vorname", "nachname"],
    Flanschmonteur: ["vorname", "nachname", "gebDatum", "prufungsdatum", "prufungsort", "gultigBis", "ausgestelltAm", "prufer"],
    Verschraubungsmonteur: ["vorname", "nachname", "gebDatum", "prufungsdatum", "prufungsort", "gultigBis", "ausgestelltAm", "prufer"],
    PSAgA: ["vorname", "nachname", "gebDatum", "prufungsdatum", "prufungsort", "gultigBis", "ausgestelltAm", "prufer"],
    Hubarbeitsbühnen: ["vorname", "nachname", "gebDatum", "prufungsdatum", "prufungsort", "gultigBis", "ausgestelltAm", "prufer"],
    "Anschlagen von Lasten": ["vorname", "nachname", "gebDatum", "prufungsdatum", "prufungsort", "gultigBis", "ausgestelltAm", "prufer"],
    "Atemschutzgeräte": ["vorname", "nachname", "gebDatum", "prufungsdatum", "prufungsort", "gultigBis", "ausgestelltAm", "prufer"],
    "Brandschutzbeauftragter": ["vorname", "nachname", "gebDatum", "prufungsdatum", "prufungsort", "ausgestelltAm"],
    SiGeKo: ["vorname", "nachname", "gebDatum", "prufungsdatum", "prufungsort", "ausgestelltAm"],
    "SCC Document 16": ["vorname", "nachname", "gebDatum", "prufungsdatum", "prufungsort", "gultigBis", "ausgestelltAm", "prufer"],
    "EHS Manager": ["vorname", "nachname", "gebDatum", "prufungsdatum", "prufungsort", "gultigBis", "ausgestelltAm", "prufer"],
    "Sifa LEK 1-5": ["geschlecht", "vorname", "nachname", "gebDatum", "startdatum", "enddatum", "ausgestelltAm", "teilnehmerId"],
    "Sifa LEK 6": ["geschlecht", "vorname", "nachname", "gebDatum", "startdatum", "enddatum", "ausgestelltAm", "teilnehmerId"],
    "Sifa Nachweis": ["geschlecht", "vorname", "nachname", "gebDatum", "startdatum", "ausgestelltAm", "teilnehmerId"],
    "Sifa Abschluss": ["geschlecht", "vorname", "nachname", "gebDatum", "startdatum", "enddatum", "ausgestelltAm", "teilnehmerId"],
    "Sifa Lernbegleiter": ["geschlecht", "vorname", "nachname", "gebDatum", "startdatum", "enddatum", "ausgestelltAm", "teilnehmerId"],
    "Sachkundenachweis TRGS 519": [
      "vorname", "nachname", "gebDatum", "geburtsort",
      "startdatum", "endPrufungsdatum", "prufungsort",
      "ausgestelltAm", "prufer",
    ],
  };

  const optionalFields = {
    "Sachkundenachweis TRGS 519": ["gultigBis", "teilnehmerNr"],
    // "Sifa LEK 6": ["branche"],
    // "Sifa Abschluss": ["branche"],
  };

  // Function to get validity period information
  const getValidityInfo = (certType) => {
    if (certType === "SCC Document 16") {
      return {
        period: "5 years",
        message: "SCC Document 16 certificates are valid for 5 years."
      };
    }
    if (certType === "Sachkundenachweis TRGS 519") {
      return {
        period: "6 years",
        message: "ASI Sachkundenachweis TRGS 519 is valid for 6 years from the issue date."
      };
    }
    const reqFields = requiredFields[certType] || [];
    const optFields = optionalFields[certType] || [];
    if (reqFields.includes("gultigBis") || optFields.includes("gultigBis")) {
      return {
        period: "1 year",
        message: "This certificate is generally valid for 1 year."
      };
    }
    return null;
  };

  useEffect(() => {
    // Initialize templates with certificate types
    setTemplates(certificateTypes.map(type => ({
      name: type,
      dummyData: dummyData,
    })));
  }, [t]);

  const handlePreview = async (templateName) => {
    const token = localStorage.getItem("token");
    if (!token) {
      setStatus(t("templates.errors.sessionExpired"));
      navigate("/");
      return;
    }

    setIsGenerating(prev => ({ ...prev, [templateName]: true }));
    setStatus("");

    const formData = new FormData();

    try {
      // Define explicit headers matching backend column_mapping
      const headers = {};
      const currentFields = requiredFields[templateName] || [];
      currentFields.forEach(field => {
        switch (field) {
          case "geschlecht":
            headers[field] = "geschlecht";
            break;
          case "vorname":
            headers[field] = "vorname";
            break;
          case "nachname":
            headers[field] = "nachname";
            break;
          case "gebDatum":
            headers[field] = "geb.-datum";
            break;
          case "startdatum":
            headers[field] = "startdatum";
            break;
          case "enddatum":
            headers[field] = "enddatum";
            break;
          case "prufungsdatum":
            headers[field] = "prüfungsdatum";
            break;
          case "endPrufungsdatum":
            headers[field] = "__endPrufungsdatum__";
            break;
          case "prufungsort":
            headers[field] = "prüfungsort";
            break;
          case "gultigBis":
            headers[field] = "gültig bis";
            break;
          case "ausgestelltAm":
            headers[field] = "ausgestellt am";
            break;
          case "prufer":
            headers[field] = "prüfer";
            break;
          case "geburtsort":
            headers[field] = "geburtsort";
            break;
          case "bescheidDatum":
            headers[field] = "bescheid_datum";
            break;
          case "aktenzeichen":
            headers[field] = "aktenzeichen";
            break;
          case "teilnehmerNr":
            headers[field] = "zertifikatsnr";
            break;
          case "teilnehmerId":
            headers[field] = "teilnehmer_id";
            break;
          default:
            break;
        }
      });

      // Map dummy data to match backend headers
      const mappedRow = {};
      currentFields.forEach(field => {
        if (field === "endPrufungsdatum") {
          const shared = dummyData.endPrufungsdatum || dummyData.enddatum || dummyData.prufungsdatum || "";
          mappedRow["enddatum"] = shared;
          mappedRow["prüfungsdatum"] = shared;
          return;
        }
        const backendField = headers[field];
        mappedRow[backendField] = dummyData[field] || "";
      });

      // Debug: Log the data being sent
      console.log("Number of Rows to Process:", 1);
      console.log("Mapped Row for Excel:", mappedRow);

      // Generate Excel file with dummy data
      const excelHeaders = Object.keys(headers).flatMap((field) =>
        field === "endPrufungsdatum" ? ["enddatum", "prüfungsdatum"] : [headers[field]]
      );
      const ws = XLSX.utils.json_to_sheet([mappedRow], { header: excelHeaders });
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Certificates");
      const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
      const excelFile = new Blob([excelBuffer], { type: "application/octet-stream" });

      formData.append("file", excelFile, "dummy_certificates.xlsx");
      formData.append("certificateType", templateName);
      formData.append("printableVersion", "false"); // Default to Digital version for preview
      formData.append("organization", "Auraplan Academy");
      formData.append("includeBarcode", "false"); // No blockchain for preview

      // Debug: Log the FormData entries
      for (let pair of formData.entries()) {
        console.log("FormData Entry:", pair[0], pair[1]);
      }

      const response = await axios.post(`${Config.API_BASE_URL}/generate`, formData, {
        headers: {
          "x-access-token": token,
        },
        responseType: "blob",
      });

      // Dynamic filename for the preview
      const formattedDate = getPreviewDownloadDateDdmmyy(templateName, dummyData, requiredFields);
      const typeSlug = getDownloadTypeSlug(templateName);

      const fileName =
        templateName === "Namensschild"
          ? `preview_namensschild_${formattedDate}.pdf`
          : `preview_${typeSlug}_${formattedDate}.zip`;

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setStatus(t("templates.success.generated"));
    } catch (error) {
      console.error("Error generating preview:", error);
      if (error.response?.status === 401) {
        setStatus(t("templates.errors.sessionExpired"));
        navigate("/");
      } else if (error.response?.status === 400) {
        const errorText = await error.response.data.text();
        setStatus(errorText || t("templates.errors.generationFailed"));
      } else if (error.message.includes("Network Error")) {
        setStatus(t("templates.errors.internetIssue"));
      } else {
        setStatus(t("templates.errors.serverErrorAdmin"));
      }
    } finally {
      setIsGenerating(prev => ({ ...prev, [templateName]: false }));
    }
  };

  const handleAddTemplateClick = () => {
    setShowUpload(true);
    fileInputRef.current?.click();
  };

  const handleFileUpload = (e) => {
    const uploadedFile = e.target.files[0];
    if (!uploadedFile) return;

    // Simulate upload process
    setStatus(t("templates.success.uploaded"));
    setShowUpload(false);

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <div className="templates-container">
      <header className="templates-header">
        <h1>{t("templates.title")}</h1>
      </header>
      <main className="templates-main">
        <section className="templates-section animate-fade-in">
          <h2>{t("templates.header")}</h2>
          {status && <div className="status-toast">{status}</div>}
          <div className="templates-grid">
            {templates.map(template => (
              <div key={template.name} className="template-card">
                <h3>{t(`createCertificate.certificateTypes.${template.name}`)}</h3>
                {getValidityInfo(template.name) && (
                  <div className="validity-info">
                    <span className="validity-label">Validity:</span>
                    <span className="validity-period">{getValidityInfo(template.name).period}</span>
                  </div>
                )}
                <button
                  className="preview-btn"
                  onClick={() => handlePreview(template.name)}
                  disabled={isGenerating[template.name]}
                >
                  {isGenerating[template.name] ? (
                    <>
                      <Loader size={16} className="btn-icon spin" />
                      {t("templates.messages.generating")}
                    </>
                  ) : (
                    <>
                      <Eye size={16} className="btn-icon" />
                      {t("templates.previewButton")}
                    </>
                  )}
                </button>
              </div>
            ))}
            {/* Add Template Card */}
            <div className="template-card add-template-card" onClick={handleAddTemplateClick}>
              <Plus size={24} className="add-icon" />
              <h3>{t("templates.addTemplateButton")}</h3>
              <input
                type="file"
                ref={fileInputRef}
                style={{ display: "none" }}
                onChange={handleFileUpload}
                accept=".pdf,.doc,.docx"
              />
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default Templates;